import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../utils/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  ArrowLeft, Plus, Trash2, Loader2, Sparkles, Home, Trophy,
  Layers, Clock, Pencil, Copy, Film, Type
} from 'lucide-react';
import { ReelEditor } from '../components/editor/reels/ReelEditor';

const ICONS = [
  { id: 'sparkles', Icon: Sparkles },
  { id: 'home', Icon: Home },
  { id: 'trophy', Icon: Trophy },
  { id: 'film', Icon: Film },
];

function TemplateList({ templates, onEdit, onDuplicate, onDelete, onNew, loading }) {
  return (
    <div data-testid="template-list">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold">Reel Templates</h2>
          <p className="text-sm text-muted-foreground">{templates.length} templates</p>
        </div>
        <Button size="sm" onClick={onNew} data-testid="new-template-btn">
          <Plus className="h-4 w-4 mr-1" /> New Template
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : templates.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Film className="h-10 w-10 mx-auto mb-3 opacity-30" />
          <p>No reel templates yet</p>
          <Button size="sm" className="mt-3" onClick={onNew}>Create your first template</Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map((tpl) => {
            const IconComp = ICONS.find(i => i.id === tpl.icon)?.Icon || Sparkles;
            const tracks = tpl.tracks || tpl.tracks_config || [];
            const layers = tpl.layers || tpl.overlays_config || [];
            const thumbUrl = tpl.thumbnail_url || tracks.find(t => t.source_url)?.source_url;
            return (
              <div
                key={tpl.template_id}
                className="border border-border/50 rounded-xl overflow-hidden bg-card hover:shadow-md transition-shadow group"
                data-testid={`template-card-${tpl.template_id}`}
              >
                {/* Thumbnail / preview strip */}
                <div className="relative h-32 bg-muted/30">
                  {thumbUrl ? (
                    <img src={thumbUrl} alt={tpl.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: `${tpl.color}15` }}>
                      <IconComp className="h-10 w-10 opacity-30" style={{ color: tpl.color }} />
                    </div>
                  )}
                  <div className="absolute top-0 left-0 right-0 h-1.5" style={{ backgroundColor: tpl.color }} />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                    <div className="flex gap-0.5 h-3">
                      {tracks.slice(0, 8).map((slot, idx) => (
                        <div key={idx} className="flex-1 rounded-sm" style={{ backgroundColor: tpl.color, opacity: 0.5 + (idx * 0.07) }} />
                      ))}
                    </div>
                  </div>
                </div>

                <div className="p-3">
                  <div className="flex items-start gap-2 mb-2">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${tpl.color}15` }}>
                      <IconComp className="h-4 w-4" style={{ color: tpl.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-sm truncate">{tpl.name}</h3>
                      <p className="text-[11px] text-muted-foreground line-clamp-1">{tpl.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-[11px] text-muted-foreground mb-3">
                    <span className="flex items-center gap-1"><Layers className="h-3 w-3" /> {tracks.length} slides</span>
                    <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {tpl.total_duration}s</span>
                    <span className="flex items-center gap-1"><Type className="h-3 w-3" /> {layers.length} layers</span>
                  </div>
                  <div className="flex gap-1.5">
                    <Button variant="outline" size="sm" className="flex-1 text-xs" onClick={() => onEdit(tpl)} data-testid={`edit-${tpl.template_id}`}>
                      <Pencil className="h-3 w-3 mr-1" /> Edit
                    </Button>
                    <Button variant="outline" size="sm" className="text-xs" onClick={() => onDuplicate(tpl)} title="Duplicate">
                      <Copy className="h-3 w-3" />
                    </Button>
                    <Button variant="outline" size="sm" className="text-xs text-destructive" onClick={() => onDelete(tpl.template_id)} title="Delete">
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function AdminReelTemplatesPage() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = list, 'new' | template_id = editor

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/reels/templates');
      setTemplates(res.data.templates || []);
    } catch (e) {
      toast.error('Failed to load templates');
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  const handleDuplicate = async (tpl) => {
    try {
      const payload = {
        name: `${tpl.name} (Copy)`,
        description: tpl.description,
        category: tpl.category,
        color: tpl.color,
        icon: tpl.icon,
        tracks: tpl.tracks || tpl.tracks_config || [],
        layers: tpl.layers || tpl.overlays_config || [],
        audio: tpl.audio || tpl.audio_config || {},
      };
      await apiClient.post('/reels/templates', payload);
      toast.success('Template duplicated');
      loadTemplates();
    } catch (e) {
      toast.error('Failed to duplicate');
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Delete this template? This cannot be undone.')) return;
    try {
      await apiClient.delete(`/reels/templates/${templateId}`);
      toast.success('Template deleted');
      loadTemplates();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  // Editor mode — uses the full ReelEditor
  if (editing) {
    return (
      <div className="h-screen flex flex-col bg-background" data-testid="admin-template-editor-wrapper">
        <div className="flex-1 p-4 overflow-auto">
          <ReelEditor
            mode="template"
            templateId={editing === 'new' ? null : editing}
            onBack={() => { setEditing(null); loadTemplates(); }}
            onSave={() => { loadTemplates(); }}
          />
        </div>
      </div>
    );
  }

  // List mode
  return (
    <div className="min-h-screen bg-background" data-testid="admin-reel-templates-page">
      <div className="border-b border-border/50 px-4 py-2 flex items-center gap-3 bg-card/50">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <Film className="h-4 w-4 text-purple-500" />
        <h1 className="text-base font-semibold">Reel Template Admin</h1>
      </div>

      <div className="max-w-6xl mx-auto p-6">
        <TemplateList
          templates={templates}
          onEdit={(tpl) => setEditing(tpl.template_id)}
          onDuplicate={handleDuplicate}
          onDelete={handleDelete}
          onNew={() => setEditing('new')}
          loading={loading}
        />
      </div>
    </div>
  );
}
